import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  createMailSnapshot,
  fetchMailAttachmentBlob,
  getMailSnapshot,
  getMailThread,
  sundaeMessageUrl,
  type MailAddress,
  type MailSnapshot,
  type MailThreadMessage,
} from '@/api/mail';
import { useAuth } from '@/auth/useAuth';
import { AttachmentList } from './AttachmentList';
import { MessageBody } from './MessageBody';
import {
  extractStylesAndBody,
  referencedAttachmentIds,
  resolveAttachmentPlaceholders,
  textToHtml,
  type SanitizedEmail,
} from './emailHtml';
import { useMailPreviewStore } from './mailPreviewStore';

function formatAddress(address: MailAddress): string {
  if (address.name && address.address) return `${address.name} <${address.address}>`;
  return address.name || address.address || 'unknown';
}

function formatAddressList(addresses: MailAddress[]): string {
  return addresses.map(formatAddress).join(', ');
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Read-only view of one mail SNAPSHOT (plan §8b). No reply, no archive, no
 * flags — this app holds a copy of a message, not a mailbox. The thread
 * expander lists sibling headers live through the bridge; opening one takes
 * its own grant and snapshot, which is why it is a button and not a link.
 */
export function MailPreviewDialog() {
  const open = useMailPreviewStore((s) => s.open);
  const messageId = useMailPreviewStore((s) => s.messageId);
  const journeyId = useMailPreviewStore((s) => s.journeyId);
  const label = useMailPreviewStore((s) => s.label);
  const closePreview = useMailPreviewStore((s) => s.closePreview);
  const openPreview = useMailPreviewStore((s) => s.openPreview);
  const { can } = useAuth();

  const [snapshot, setSnapshot] = useState<MailSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canPreview = can('orders:read:mail');

  useEffect(() => {
    // `/api/mail/*` is gated on orders:read:mail. A store entry can only come from
    // a chip that already checked, but the fetch is the thing that must not
    // happen — so the check lives here too, ahead of it.
    if (!open || !messageId || !canPreview) {
      setSnapshot(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMailSnapshot(messageId)
      .then((result) => {
        if (!cancelled) setSnapshot(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load this email');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, messageId, canPreview]);

  if (!canPreview) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : closePreview())}>
      {/* h (not max-h): a DEFINITE container height is what makes the flexed
          body's own height resolve; under max-h the percentages fall back to
          auto and the message just clips. */}
      <DialogContent className="ml-auto flex h-[92dvh] w-[95vw] max-w-6xl flex-col overflow-hidden max-md:h-dvh max-md:max-h-none max-md:w-screen max-md:max-w-none max-md:rounded-none">
        <DialogTitle className="sr-only">{snapshot?.subject || label || 'Email'}</DialogTitle>
        {loading && (
          <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading {label || 'email'}…
          </div>
        )}
        {error && !loading && (
          <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-danger-ink">
            {error}
          </div>
        )}
        {snapshot && !loading && !error && (
          <SnapshotView
            snapshot={snapshot}
            journeyId={journeyId}
            onOpenMessage={(id) => openPreview(id, { journeyId, label: null })}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SnapshotView({
  snapshot,
  journeyId,
  onOpenMessage,
}: {
  snapshot: MailSnapshot;
  journeyId: string | null;
  onOpenMessage: (messageId: string) => void;
}) {
  const [inlineUrls, setInlineUrls] = useState<Map<string, string>>(new Map());
  const inlineUrlsRef = useRef<string[]>([]);

  // `attachment:<id>` placeholders can't be loaded by the iframe directly (the
  // blob route needs a bearer header), so fetch each referenced part once and
  // substitute object URLs before the body is built.
  useEffect(() => {
    const html = snapshot.html_sanitized;
    if (!html) return;
    const ids = referencedAttachmentIds(html);
    if (ids.length === 0) return;
    let cancelled = false;
    const created: string[] = [];
    Promise.all(
      ids.map(async (id) => {
        try {
          const blob = await fetchMailAttachmentBlob(snapshot.message_id, id);
          const url = URL.createObjectURL(blob);
          created.push(url);
          return [id, url] as const;
        } catch {
          return null;
        }
      })
    ).then((entries) => {
      if (cancelled) {
        created.forEach((url) => URL.revokeObjectURL(url));
        return;
      }
      inlineUrlsRef.current = created;
      setInlineUrls(new Map(entries.filter((e): e is readonly [string, string] => e !== null)));
    });
    return () => {
      cancelled = true;
      inlineUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      inlineUrlsRef.current = [];
    };
  }, [snapshot.message_id, snapshot.html_sanitized]);

  const body = useMemo<SanitizedEmail | null>(() => {
    if (snapshot.html_sanitized) {
      return extractStylesAndBody(resolveAttachmentPlaceholders(snapshot.html_sanitized, inlineUrls));
    }
    if (snapshot.text_plain) {
      return textToHtml(snapshot.text_plain);
    }
    return null;
  }, [snapshot.html_sanitized, snapshot.text_plain, inlineUrls]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="shrink-0 pr-8">
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[.1em] text-muted-foreground">Source document{journeyId ? ` · Order #${journeyId}` : ''}</div>
        <h2 className="text-base font-semibold leading-snug">{snapshot.subject || '(no subject)'}</h2>
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          <dt className="font-medium">From</dt>
          <dd className="truncate">{formatAddress(snapshot.from)}</dd>
          {snapshot.to.length > 0 && (
            <>
              <dt className="font-medium">To</dt>
              <dd className="truncate">{formatAddressList(snapshot.to)}</dd>
            </>
          )}
          {snapshot.cc.length > 0 && (
            <>
              <dt className="font-medium">Cc</dt>
              <dd className="truncate">{formatAddressList(snapshot.cc)}</dd>
            </>
          )}
          <dt className="font-medium">Date</dt>
          <dd>{formatDate(snapshot.sent_at)}</dd>
        </dl>
        <div className="mt-2 flex items-center gap-3">
          <a
            href={sundaeMessageUrl(snapshot.message_id)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <ExternalLink className="h-3 w-3" /> Open in sundae
          </a>
          <span className="text-xs text-muted-foreground">
            Snapshot taken {formatDate(snapshot.snapshot_at)} by {snapshot.granted_by}
          </span>
        </div>
      </header>

      <Separator className="my-3 shrink-0" />

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1.3fr)_minmax(300px,1fr)] gap-6 overflow-y-auto max-md:grid-cols-1">
        <section aria-label="Email body" className="min-w-0">
        {body ? (
          <MessageBody sanitizedHtml={body} />
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">This message has no body.</p>
        )}

        </section>
        <aside aria-label="Attachments and related messages" className="min-w-0 border-l border-border pl-6 max-md:border-l-0 max-md:border-t max-md:p-0 max-md:pt-5">
        <h3 className="text-sm font-semibold">Attachments & related messages</h3>
        <AttachmentList
          messageId={snapshot.message_id}
          attachments={snapshot.attachments}
          className="mt-4"
        />

        <ThreadExpander
          messageId={snapshot.message_id}
          journeyId={journeyId}
          onOpenMessage={onOpenMessage}
        />
        </aside>
      </div>
    </div>
  );
}

/**
 * Header-only sibling list, fetched live through the bridge and filtered to
 * the journey's vendor domains (plan §12.4: message scope only, never a
 * whole-thread grant). "Snapshot" takes the grant for one row and opens it.
 */
function ThreadExpander({
  messageId,
  journeyId,
  onOpenMessage,
}: {
  messageId: string;
  journeyId: string | null;
  onOpenMessage: (messageId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState<MailThreadMessage[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const toggle = useCallback(async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (messages) return;
    setLoading(true);
    setError(null);
    try {
      setMessages(await getMailThread(messageId, journeyId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the thread');
    } finally {
      setLoading(false);
    }
  }, [expanded, messages, messageId, journeyId]);

  const snapshotAndOpen = async (row: MailThreadMessage) => {
    if (row.granted) {
      onOpenMessage(row.message_id);
      return;
    }
    setPendingId(row.message_id);
    try {
      await createMailSnapshot({
        message_id: row.message_id,
        journey_id: journeyId,
        kind: 'reference',
      });
      onOpenMessage(row.message_id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not snapshot that message');
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="mt-6 border-t border-border/60 pt-3">
      <button
        type="button"
        onClick={toggle}
        className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Thread
      </button>

      {expanded && (
        <div className="mt-2 space-y-1">
          {loading && (
            <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading thread…
            </div>
          )}
          {error && <div className="py-2 text-xs text-danger-ink">{error}</div>}
          {messages?.length === 0 && !loading && (
            <p className="py-2 text-xs text-muted-foreground">
              No other messages from this vendor in the thread.
            </p>
          )}
          {messages?.map((row) => (
            <div
              key={row.message_id}
              className={cn(
                'flex items-center gap-2 rounded-md px-2 py-1.5 text-xs',
                row.message_id === messageId ? 'bg-accent/60' : 'hover:bg-accent/40'
              )}
            >
              <Mail className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{row.subject || '(no subject)'}</span>
              <span className="hidden shrink-0 text-muted-foreground sm:inline">
                {row.from.name || row.from.address}
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {formatDate(row.sent_at)}
              </span>
              {row.message_id !== messageId && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 shrink-0 px-2 text-xs"
                  disabled={pendingId === row.message_id}
                  onClick={() => void snapshotAndOpen(row)}
                >
                  {pendingId === row.message_id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : row.granted ? (
                    'Open'
                  ) : (
                    'Snapshot'
                  )}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
